import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { login, verifyLoginOtp, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';
import { usePwaInstall } from '../hooks/usePwaInstall';
import { useTheme } from '../theme/ThemeContext';

/**
 * Fabbrica della pagina di login condivisa. Ogni modulo passa solo ciò che lo differenzia:
 * - `brand`: { mode: 'svg', mark: <svg/> } (icona + titolo) oppure { mode: 'image', src } (logo a immagine) oppure { mode: 'plainImage', src } (<img> semplice, classe login-logo-img)
 * - `showTitle` (default true): mostra <h1>{auth.title}</h1> (false se il logo contiene già il nome)
 * - `demoHint` (default true): mostra il suggerimento con l'utente demo
 * - `pwaInstall` (default true): mostra il pulsante "Installa l'app" quando il browser lo permette
 * - `forgotPasswordPath` (default null): percorso della pagina "password dimenticata"; se presente compare il link
 * - `themeToggle` (default false): selettore chiaro/scuro/sistema in alto nella pagina di login
 * - `privacyPolicyUrl` / `cookiePolicyUrl` (default null): se presenti, mostra un footer con i link
 *   alle informative (es. pagine ospitate su Iubenda) sotto la card di login
 * Se il backend risponde `otp_required` il login prosegue con la verifica del codice a 6 cifre.
 */
const themeIcons = {
  light: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  ),
  dark: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="18" x2="12" y2="21" />
    </svg>
  ),
};

export function createLoginPage({ brand, showTitle = true, demoHint = true, pwaInstall = true, forgotPasswordPath = null, themeToggle = false, privacyPolicyUrl = null, cookiePolicyUrl = null }) {
  function Login({ onLogin }) {
    const { t } = useLanguage();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showIosHint, setShowIosHint] = useState(false);
    const [otp, setOtp] = useState('');
    const [otpRequired, setOtpRequired] = useState(false);
    const { theme, setTheme } = useTheme();
    const { canInstall, isIosManualInstall, suggestOtherBrowser, promptInstall } = usePwaInstall();

    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      setLoading(true);
      try {
        const response = await login(email.trim(), password);
        if (response.otp_required) {
          setOtpRequired(true);
        } else {
          onLogin(response.user.token, response.user);
        }
      } catch (err) {
        setError(errorMessage(err, t('errors.invalidCredentials')));
      } finally {
        setLoading(false);
      }
    };

    const handleVerifyOtp = async (e) => {
      e.preventDefault();
      setError('');
      if (!/^\d{6}$/.test(otp.trim())) {
        setError(t('auth.otpErrorDigits'));
        return;
      }
      setLoading(true);
      try {
        const response = await verifyLoginOtp(email.trim(), otp.trim());
        onLogin(response.user.token, response.user);
      } catch (err) {
        setError(errorMessage(err, t('auth.otpErrorGeneric')));
      } finally {
        setLoading(false);
      }
    };

    const handleBackToLogin = () => {
      setOtpRequired(false);
      setOtp('');
      setError('');
    };

    return (
      <div className="login-container">
        {themeToggle && (
          <div className="theme-toggle login-theme-toggle" role="group" aria-label={t('nav.themeToggleLabel')}>
            {['light', 'dark', 'system'].map((option) => (
              <button
                key={option}
                type="button"
                className={'theme-toggle-btn' + (theme === option ? ' active' : '')}
                onClick={() => setTheme(option)}
                title={t(`nav.theme_${option}`)}
                aria-label={t(`nav.theme_${option}`)}
              >
                {themeIcons[option]}
              </button>
            ))}
          </div>
        )}
        <div className="login-card">
          <div className="login-logo">
            {brand.mode === 'plainImage' ? (
              <img src={brand.src} alt={t('auth.title')} className="login-logo-img" />
            ) : brand.mode === 'image' ? (
              <span className="brand-logo">
                <img src={brand.src} alt={t('auth.title')} />
              </span>
            ) : (
              <span className="brand-mark">{brand.mark}</span>
            )}
            {showTitle && <h1>{t('auth.title')}</h1>}
            <p>{t('auth.subtitle')}</p>
          </div>

          {error && <div className="error-message">{error}</div>}

          {otpRequired ? (
            <>
              <h2>{t('auth.otpTitle')}</h2>
              <p className="reset-hint">{t('auth.otpHint', { email })}</p>
              <p className="form-hint">{t('auth.otpValidity')}</p>
              <form onSubmit={handleVerifyOtp}>
                <div className="form-group">
                  <label htmlFor="login-otp">{t('auth.otpLabel')}</label>
                  <input
                    type="text"
                    id="login-otp"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                    placeholder={t('auth.otpPlaceholder')}
                    maxLength={6}
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    autoFocus
                    required
                  />
                </div>
                <button type="submit" className="btn btn-lg" disabled={loading}>
                  {loading ? t('auth.otpSubmitting') : t('auth.otpSubmit')}
                </button>
              </form>
              <div className="login-links">
                <button type="button" className="link-button" onClick={handleBackToLogin}>
                  {t('auth.otpBack')}
                </button>
              </div>
            </>
          ) : (
          <>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="email">{t('auth.email')}</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.emailPlaceholder')}
                required
                autoFocus
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">{t('auth.password')}</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.password')}
                required
              />
            </div>
            <button type="submit" className="btn btn-lg" disabled={loading}>
              {loading ? t('auth.accessing') : t('auth.access')}
            </button>
          </form>

          {forgotPasswordPath && (
            <div className="login-links">
              <Link to={forgotPasswordPath} className="link-button">
                {t('auth.forgotPassword')}
              </Link>
            </div>
          )}

          {demoHint && (
            <div className="login-hint">
              {t('auth.demoHint')} <strong>test@example.com</strong> / <strong>password123</strong>
            </div>
          )}

          {pwaInstall && (canInstall || isIosManualInstall) && (
            <div className="install-app">
              <button
                type="button"
                className="btn btn-secondary btn-install-app"
                onClick={canInstall ? promptInstall : () => setShowIosHint((v) => !v)}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
                  <path d="M12 3v12m0 0-4-4m4 4 4-4M5 21h14" />
                </svg>
                {canInstall ? t('auth.installApp') : t('auth.installAppIos')}
              </button>
              {isIosManualInstall && showIosHint && (
                <p className="install-app-hint">{t('auth.installAppIosHint')}</p>
              )}
            </div>
          )}

          {pwaInstall && suggestOtherBrowser && (
            <p className="install-app-hint install-app-hint-standalone">
              {t('auth.installAppOtherBrowser')}
            </p>
          )}
          </>
          )}

          {(privacyPolicyUrl || cookiePolicyUrl) && (
            <div className="login-legal-links">
              {privacyPolicyUrl && (
                <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">
                  {t('auth.privacyPolicy')}
                </a>
              )}
              {privacyPolicyUrl && cookiePolicyUrl && <span> · </span>}
              {cookiePolicyUrl && (
                <a href={cookiePolicyUrl} target="_blank" rel="noopener noreferrer">
                  {t('auth.cookiePolicy')}
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  return Login;
}
